import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Trophy } from 'lucide-react';
import { statusCampeonato } from '../../lib/statusCampeonato';
import { formatarDataBR } from '../../lib/datas';

/**
 * ETAPA VISUAL 6 — PÁGINAS PÚBLICAS (primeira impressão)
 *
 * A landing mostrava o hero como uma coluna centralizada de ~740px de altura:
 * o primeiro campeonato só aparecia a ~988px, abaixo da primeira dobra. O
 * visitante via "vitrine", não "produto em uso".
 *
 * Aqui está a peça que resolve isso: um card do PRÓXIMO evento para ficar AO
 * LADO da promessa. Ele usa só o que a API pública devolve
 * (`name`, `date`, `address`, `status`, `banner_path`) — os "inscritos" que a
 * auditoria sugeria não existem nesse payload, e inventar esse número seria
 * mentir na vitrine.
 *
 * O selo de status reusa `lib/statusCampeonato.js` (Etapa 3): a mesma cor
 * significa a mesma coisa aqui e dentro do sistema.
 */

/** Contagem regressiva em texto curto. `dias` vem calculado pela tela. */
function textoContagem(dias) {
  if (dias === null || dias === undefined) return null;
  if (dias === 0) return 'É hoje';
  if (dias === 1) return 'É amanhã';
  if (dias > 1) return `Faltam ${dias} dias`;
  return null;
}

export function CardProximoEvento({ evento, dias, href, apiUrl = '' }) {
  if (!evento) return null;

  const contagem = textoContagem(dias);
  const selo = statusCampeonato(evento.status);

  return (
    <div
      className="overflow-hidden rounded-2xl border border-[#27272A] bg-[#141414] shadow-2xl"
      data-testid="card-proximo-evento"
    >
      <div className="relative h-36 overflow-hidden bg-[#1A1A1A]">
        {evento.banner_path ? (
          <img
            src={`${apiUrl}/api/files/${evento.banner_path}`}
            alt={evento.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]">
            <Trophy className="h-12 w-12 text-[#27272A]" aria-hidden="true" />
          </div>
        )}

        {contagem && (
          <span className="absolute left-3 top-3 rounded-full bg-[#D4AF37] px-3 py-1 text-micro font-bold text-black">
            {contagem}
          </span>
        )}
      </div>

      <div className="p-5">
        <span className={selo.selo}>{selo.rotulo}</span>

        <h3 className="mt-2 line-clamp-2 font-['Chivo'] text-lg font-bold leading-tight text-white">
          {evento.name}
        </h3>

        <div className="mt-3 space-y-2 text-sm">
          {evento.date && (
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <Calendar size={16} className="shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <span className="truncate">{formatarDataBR(evento.date)}</span>
            </div>
          )}
          {evento.address && (
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <MapPin size={16} className="shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <span className="truncate">{evento.address}</span>
            </div>
          )}
        </div>

        <Link
          to={href}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#D4AF37] px-4 py-2.5 font-bold text-black transition-colors hover:bg-[#E5C158]"
          data-testid="card-proximo-evento-abrir"
        >
          Ver detalhes <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

/**
 * Faixa de resumo do evento — uma linha com o essencial (data · local ·
 * federação · categorias · inscritos) logo abaixo do título, antes do resto do
 * conteúdo. Antes essa informação ficava espalhada na coluna da direita, sem
 * hierarquia.
 *
 * Recebe uma lista de `{ icone, rotulo, valor }` e ignora os itens vazios, para
 * a tela não precisar montar condicional por item.
 */
export function FaixaResumoEvento({ itens = [], className = '', direcao = 'linha', moldura = true }) {
  const visiveis = itens.filter((i) => i && i.valor !== null && i.valor !== undefined && i.valor !== '');
  if (visiveis.length === 0) return null;

  /* `direcao="coluna"` empilha os itens (um embaixo do outro) em vez de deixá-los
     lado a lado. É usado no cabeçalho do evento, onde a faixa fica AO LADO da
     imagem numa coluna estreita.
     `moldura={false}` remove a caixa (borda + fundo + respiro interno) e deixa os
     itens SOLTOS, no mesmo plano do título e da situação. */
  const emColuna = direcao === 'coluna';
  const caixa = moldura ? 'rounded-xl border border-[#27272A] bg-[#141414] px-5 py-4' : '';

  return (
    <div
      className={`flex ${
        emColuna ? 'flex-col items-start gap-y-2.5' : 'flex-wrap items-center gap-x-6 gap-y-3'
      } ${caixa} ${className}`}
      data-testid="faixa-resumo-evento"
    >
      {visiveis.map((item, indice) => {
        const Icone = item.icone;
        return (
          <span key={`${item.rotulo || 'item'}-${indice}`} className="flex items-center gap-2 text-sm">
            {Icone && <Icone size={16} className="shrink-0 text-[#D4AF37]" aria-hidden="true" />}
            {item.rotulo && <span className="text-[#71717A]">{item.rotulo}</span>}
            <span className="font-medium text-white">{item.valor}</span>
          </span>
        );
      })}
    </div>
  );
}
